import { PaymentMethod } from '../../models';
import { ALL_PAYMENT_METHODS, GuestInfo, PaymentEntry, PaymentMode, Step } from './types';

const CHECKOUT_STORAGE_KEY = 'babychic-checkout-state';
const CONFIRMATION_STORAGE_PREFIX = 'babychic-order-confirmation-';

export interface PersistedCheckoutState {
  step?: Step;
  guestInfo?: GuestInfo;
  paymentEntries?: PaymentEntry[];
  paymentMode?: PaymentMode;
  observation?: string;
  selectedSellerId?: number;
  idempotencyKey?: string;
  cartTotal?: number;
}

export interface OrderConfirmationGuest {
  name: string;
  phone: string;
  email: string;
}

export interface OrderConfirmationItem {
  name: string;
  variant: string;
  quantity: number;
}

export interface OrderConfirmation {
  orderId: number;
  whatsAppUrl: string;
  orderedAt: string;
  paymentMethods: PaymentMethod[];
  sellerName?: string;
  guest?: OrderConfirmationGuest;
  items?: OrderConfirmationItem[];
  total?: number;
}

export function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}-${Math.random().toString(36).slice(2, 12)}`;
}

function normalizeGuestInfo(guestInfo?: GuestInfo): GuestInfo | undefined {
  if (!guestInfo) return guestInfo;

  const rawBabies = (guestInfo as { babies?: unknown }).babies;
  const babies = Array.isArray(rawBabies) ? (rawBabies as GuestInfo['babies']) : [];
  const lookupConfirmed = guestInfo.lookupConfirmed === true;
  const name = lookupConfirmed && guestInfo.name.includes('*') ? '' : guestInfo.name;

  return { ...guestInfo, name, babies, lookupConfirmed };
}

function normalizePaymentEntries(entries?: PaymentEntry[]): PaymentEntry[] | undefined {
  if (!Array.isArray(entries)) return undefined;
  const supported = entries.every((e) => ALL_PAYMENT_METHODS.includes(e.paymentMethod));
  if (!supported) return undefined;
  return entries.map((e) => ({ ...e, id: e.id ?? generateId() }));
}

export function loadPersistedCheckoutState(): PersistedCheckoutState {
  try {
    const raw = sessionStorage.getItem(CHECKOUT_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const result = parsed as PersistedCheckoutState;
    if ((result.step as string) === 'seller') result.step = 'payment';
    result.guestInfo = normalizeGuestInfo(result.guestInfo);
    result.paymentEntries = normalizePaymentEntries(result.paymentEntries);
    return result;
  } catch {
    return {};
  }
}

export function savePersistedCheckoutState(state: PersistedCheckoutState) {
  try {
    sessionStorage.setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(state));
  } catch {
  }
}

export function clearPersistedCheckoutState() {
  try {
    sessionStorage.removeItem(CHECKOUT_STORAGE_KEY);
  } catch {
  }
}

export function saveOrderConfirmation(confirmation: OrderConfirmation) {
  try {
    sessionStorage.setItem(
      `${CONFIRMATION_STORAGE_PREFIX}${confirmation.orderId}`,
      JSON.stringify(confirmation)
    );
  } catch {
  }
}

export function loadOrderConfirmation(orderId: number): OrderConfirmation | null {
  try {
    const raw = sessionStorage.getItem(`${CONFIRMATION_STORAGE_PREFIX}${orderId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<OrderConfirmation>;
    if (
      parsed.orderId !== orderId ||
      typeof parsed.whatsAppUrl !== 'string' ||
      typeof parsed.orderedAt !== 'string' ||
      !Array.isArray(parsed.paymentMethods)
    ) {
      return null;
    }
    return parsed as OrderConfirmation;
  } catch {
    return null;
  }
}
